import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, Button, FormField, Spinner, Badge } from '../components/ui';
import { Link } from 'react-router-dom';

const PLAN_LABELS = { trial:'Free Trial', starter:'Starter', landlord:'Landlord', portfolio:'Portfolio' };
const PLAN_LIMITS = { trial:3, starter:5, landlord:20, portfolio:'Unlimited' };

function formatDate(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function Profile() {
  const { refreshUser } = useAuth();
  const [profile, setProfile]   = useState(null);
  const [billing, setBilling]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [form, setForm]         = useState({ full_name:'', phone:'', company_name:'' });

  useEffect(() => {
    Promise.all([
      fetch('/api/profile', { headers:{ Authorization:`Bearer ${localStorage.getItem('token')}`, ...(localStorage.getItem('lc_access_code') ? {'X-Access-Code': localStorage.getItem('lc_access_code')} : {}) }}).then(r=>r.json()),
      fetch('/api/billing/status', { headers:{ Authorization:`Bearer ${localStorage.getItem('token')}`, ...(localStorage.getItem('lc_access_code') ? {'X-Access-Code': localStorage.getItem('lc_access_code')} : {}) }}).then(r=>r.json()),
    ]).then(([p, b]) => {
      setProfile(p); setBilling(b);
      setForm({ full_name: p.full_name || '', phone: p.phone || '', company_name: p.company_name || '' });
      setLoading(false);
    });
  }, []);

  async function handleSave() {
    setSaving(true); setSaved(false);
    try {
      await fetch('/api/profile', {
        method: 'PUT',
        headers: { 'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(form),
      });
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...user, full_name: form.full_name }));
      refreshUser();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally { setSaving(false); }
  }

  if (loading) return <Spinner />;

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const isTrial = billing?.is_trial;

  return (
    <div className="fade-up" style={{ maxWidth: 600 }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, fontWeight: 300 }}>Your <em>Profile</em></h1>
        <p style={{ color: 'var(--ink-3)', marginTop: 4 }}>Manage your account details</p>
      </div>

      {/* Profile form */}
      <Card style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 17, marginBottom: 20 }}>Account details</h3>
        <FormField label="Full name">
          <input value={form.full_name} onChange={set('full_name')} placeholder="Your full name" />
        </FormField>
        <FormField label="Email">
          <input value={profile?.email} disabled style={{ opacity: 0.6, cursor: 'not-allowed' }} />
          <p style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 3 }}>Email cannot be changed — it's your login</p>
        </FormField>
        <FormField label="Phone (optional)">
          <input value={form.phone} onChange={set('phone')} placeholder="+44 7700 000000" />
        </FormField>
        <FormField label="Company name (optional)">
          <input value={form.company_name} onChange={set('company_name')} placeholder="e.g. Smith Properties Ltd" />
        </FormField>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
          <Button loading={saving} onClick={handleSave}>Save changes</Button>
          {saved && <span style={{ fontSize: 13, color: 'var(--accent)' }}>✅ Saved</span>}
        </div>
      </Card>

      {/* Subscription */}
      <Card>
        <h3 style={{ fontSize: 17, marginBottom: 16 }}>Subscription</h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div>
            <p style={{ fontWeight: 600, fontSize: 16 }}>{PLAN_LABELS[billing?.plan] || 'Free Trial'}</p>
            <p style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2 }}>
              {billing?.property_count} / {PLAN_LIMITS[billing?.plan] || 3} properties used
            </p>

            {/* Trial messages */}
            {isTrial && billing?.trial_days_left > 0 && (
              <p style={{ fontSize: 13, color: 'var(--warn)', marginTop: 4 }}>
                ⏳ {billing.trial_days_left} days left in free trial
              </p>
            )}
            {isTrial && billing?.trial_days_left === 0 && (
              <p style={{ fontSize: 13, color: 'var(--danger)', marginTop: 4 }}>
                🚨 Trial expired — upgrade to continue
              </p>
            )}

            {/* Renewal date */}
            {!isTrial && billing?.renews_at && billing?.sub_status === 'active' && !billing?.cancel_at_period_end && (
              <p style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4 }}>
                🔄 Renews on {formatDate(billing.renews_at)}
              </p>
            )}
            {!isTrial && billing?.cancel_at_period_end && billing?.renews_at && (
              <p style={{ fontSize: 13, color: 'var(--warn)', marginTop: 4 }}>
                ⚠️ Cancels on {formatDate(billing.renews_at)}
              </p>
            )}
            {!isTrial && billing?.sub_status === 'past_due' && (
              <p style={{ fontSize: 13, color: 'var(--danger)', marginTop: 4 }}>
                ⚠️ Payment overdue
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Badge
              status={billing?.sub_status === 'active' ? 'valid' : billing?.sub_status === 'past_due' ? 'expired' : 'due_soon'}
              label={billing?.sub_status || 'trialing'}
            />
            <Link to="/billing">
              <Button size="sm" variant="ghost">
                {isTrial ? 'Upgrade →' : 'Manage billing →'}
              </Button>
            </Link>
          </div>
        </div>

        {/* Plan features */}
        <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '12px 16px' }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.05em' }}>Your plan includes</p>
          {[
            { label: `Up to ${PLAN_LIMITS[billing?.plan] || 3} properties`, ok: true },
            { label: 'Certificate tracking & reminders', ok: true },
            { label: 'Right to Rent tracking', ok: true },
            { label: 'RRA 2025 compliance tools', ok: true },
            { label: 'AI tenancy agreement checker', ok: billing?.ai_enabled },
            { label: 'HMO licensing checker', ok: billing?.plan !== 'trial' },
            { label: 'Phone support', ok: billing?.plan === 'portfolio' },
          ].map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 13, color: f.ok ? 'var(--accent)' : 'var(--border)' }}>{f.ok ? '✅' : '⬜'}</span>
              <span style={{ fontSize: 13, color: f.ok ? 'var(--ink)' : 'var(--ink-3)' }}>{f.label}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}